import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  FormBuilderFields,
  FunnelStepHeader,
  FunnelWithHelper,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  resolveFieldShortcuts,
  vehicleModelDefaultWatchedFieldNames,
  vehicleVersionDefaultWatchedFieldNames,
} from '@folio/components';
import { isFieldVisible } from '@folio/components/forms/form-builder/conditionalVisibilityUtils';
import { SectionAutoFillButton } from '@folio/components/forms/form-builder/FormBuilder';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import { getArrayFieldsResolver } from '@folio/components/forms/form-builder/getZodResolverFromFields';
import { useStepperContext } from '@folio/components/layout/StepperLayout/useStepperContext';
import { cn, useAssertedParams } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQuery } from '@tanstack/react-query';
import {
  clientWithFork,
  getNewBranchIdAfterFork,
  keyValueFieldsToObject,
  useAssetControllerCreateAsset,
  useAssetControllerEditAsset,
  useAssetControllerRemoveAsset,
  useAssetControllerReplaceAsset,
  viewSpecificControllerListPolicyAssetsQueryOptions,
  type Policy,
  type Asset as SDKAsset,
} from 'korint-sdk';
import type {
  AfterSubmitResult,
  Field,
  FieldShortcut,
  FormSectionsWithShortcuts,
} from 'korint-sdk/react';
import { shouldShowAutoFill } from '@folio/utils';
import { Building2, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useFormState, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AssetCardStatus } from '../AssetInformationCardModule/AssetCardStatus';
import { useResolvedEntityFields } from '../shared-fields/use-entity-fields';
import { isEmpty } from 'lodash';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type Asset = {
  id?: string;
  [key: string]: unknown;
};

type AssetFunnelModuleProps = {
  fields?: FieldShortcut[];
  sections?: FormSectionsWithShortcuts;
  mode?: 'stepper' | 'standalone';
  maxAssets?: number;
  minAssets?: number;
  title?: string;
  subtitle?: string;
  assetSectionTitle?: string;
  path: string;
  description?: string;
  helper?: React.ReactNode;
  showScrollNav?: boolean;
  /**
   * Optional callback invoked after the asset mutations and before `stepper.next()`. Mirrors the
   * `afterSubmit` prop of `PolicyFunnelModule` and `CustomerFunnelModule` for symmetry.
   */
  afterSubmit?: (params: {
    policyId: string;
    branchId: string;
  }) => AfterSubmitResult | Promise<AfterSubmitResult>;
};

function getWatchedFieldNames(field: Field, index: number): Record<string, string> | undefined {
  if (field.type !== 'vehicleVersion' && field.type !== 'vehicleModel') {
    return undefined;
  }

  let watchedFieldNames = {};
  if (field.type === 'vehicleVersion') {
    watchedFieldNames = {
      ...vehicleVersionDefaultWatchedFieldNames,
      ...field.watchedFieldNames,
    };
  } else if (field.type === 'vehicleModel') {
    watchedFieldNames = {
      ...vehicleModelDefaultWatchedFieldNames,
      ...field.watchedFieldNames,
    };
  }

  return Object.entries(watchedFieldNames).reduce(
    (acc, [key, value]) => {
      acc[key] = `assets.${index}.${value}`;
      return acc;
    },
    {} as Record<string, string>,
  );
}

function AssetFormTitle({
  assetData,
  policy,
  path,
  assetCount,
  minAssets,
  handleRemoveAsset,
  description,
}: {
  assetData?: SDKAsset;
  policy: Policy;
  path: string;
  assetCount: number;
  minAssets: number;
  handleRemoveAsset: () => Promise<void> | void;
  description?: string;
}) {
  const { t } = useTranslation();
  return (
    <Group justify="between" gap={0}>
      <Group gap={2}>
        <div>
          <CardTitle>{t(`${policy.productId}.assetFunnel.${path}.title`)}</CardTitle>
          {description && (
            <Text size="sm" tone="muted" className="mt-2">
              {description}
            </Text>
          )}
        </div>
        {assetData && <AssetCardStatus asset={assetData} policy={policy} hideIfInactive={true} />}
      </Group>
      {assetCount > minAssets && (
        <Button type="button" variant="destructive-ghost" size="icon" onClick={handleRemoveAsset}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </Group>
  );
}

const scrollAnchorId = (assetIndex: number, sectionIndex?: number) =>
  sectionIndex === undefined
    ? `asset-${assetIndex}`
    : `asset-${assetIndex}-section-${sectionIndex}`;

function AssetFunnelScrollNav({
  assets,
  sections,
}: {
  assets: Array<{ index: number; label: string }>;
  sections: Array<{ si: number; title: string }>;
}) {
  const [active, setActive] = useState<{ asset: number; section: number }>({
    asset: 0,
    section: sections[0]?.si ?? 0,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-observe when the anchor set changes
  useEffect(() => {
    const anchors = assets
      .flatMap(asset =>
        sections.length > 0
          ? sections.map(section => scrollAnchorId(asset.index, section.si))
          : [scrollAnchorId(asset.index)],
      )
      .map(id => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (anchors.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const topMost = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!topMost) {
          return;
        }
        const match = topMost.target.id.match(/^asset-(\d+)(?:-section-(\d+))?$/);
        if (!match) {
          return;
        }
        setActive({
          asset: Number(match[1]),
          section: match[2] !== undefined ? Number(match[2]) : 0,
        });
      },
      { rootMargin: '-25% 0px -65% 0px', threshold: 0 },
    );
    for (const anchor of anchors) {
      observer.observe(anchor);
    }
    return () => observer.disconnect();
  }, [assets, sections]);

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <nav className="sticky top-4 hidden max-h-[calc(100vh-2rem)] w-52 shrink-0 flex-col gap-1 overflow-y-auto overscroll-contain pr-1 lg:flex">
      {assets.map(asset => {
        const isActiveAsset = active.asset === asset.index;
        return (
          <Stack key={asset.index} gap={0}>
            <button
              type="button"
              onClick={() =>
                scrollTo(
                  sections.length > 0
                    ? scrollAnchorId(asset.index, sections[0].si)
                    : scrollAnchorId(asset.index),
                )
              }
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                isActiveAsset ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground',
              )}
            >
              <Building2 className="h-4 w-4 shrink-0" />
              <Text inline truncate>
                {asset.label}
              </Text>
            </button>
            {isActiveAsset && sections.length > 0 && (
              <Stack gap={0} className="ml-4 border-l pl-3">
                {sections.map(section => (
                  <button
                    key={section.si}
                    type="button"
                    onClick={() => scrollTo(scrollAnchorId(asset.index, section.si))}
                    className={cn(
                      'cursor-pointer rounded px-2 py-1 text-left text-xs transition-colors hover:text-foreground',
                      active.section === section.si
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground',
                    )}
                  >
                    {section.title}
                  </button>
                ))}
              </Stack>
            )}
          </Stack>
        );
      })}
    </nav>
  );
}

export function AssetFunnelModule({
  fields: rawFields = [],
  sections: rawSections,
  mode = 'stepper',
  title,
  subtitle,
  maxAssets = 3,
  minAssets = 1,
  path,
  description,
  helper,
  showScrollNav = false,
  afterSubmit,
}: AssetFunnelModuleProps) {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();
  const stepper = useStepperContext();
  const stepperRef = useRef(stepper);
  stepperRef.current = stepper;
  const [assetsToDelete, setAssetsToDelete] = useState<string[]>([]);

  const { data: baseBranchAssets } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policyId),
    enabled: !!policyId,
  });

  const {
    data: existingAssets,
    isLoading: isAssetsLoading,
    refetch: refetchAssets,
    isFetched: isAssetsFetched,
  } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policyId, {
      branchId,
    }),
    enabled: !!policyId,
    select: data => {
      const replacedAssetIds = new Set(
        data.data.flatMap(({ status, originAssetId }) => {
          const isReplacementAssetActive =
            !!originAssetId &&
            !['ASSET_STOPPED', 'ASSET_CANCELLED', 'ASSET_REMOVED'].includes(status);
          return isReplacementAssetActive ? [originAssetId] : [];
        }),
      );
      return data.data
        .map(asset => {
          const matchingAsset = baseBranchAssets?.data.find(a => a.id === asset.id);
          return {
            ...asset,
            status: matchingAsset?.status ?? asset.status,
            customFields: keyValueFieldsToObject(asset.customFields ?? []),
          };
        })
        .filter(asset => asset.status !== 'ASSET_STOPPED')
        .filter(asset => !replacedAssetIds.has(asset.id));
    },
  });

  const { policy, workingBranch } = usePolicyOnDashboard();

  const { mutateAsync: createAsset } = useAssetControllerCreateAsset({
    mutation: {
      onSuccess: async () => {
        await refetchAssets();
      },
    },
    client: { client: clientWithFork },
  });

  const { mutateAsync: updateAsset } = useAssetControllerEditAsset({
    mutation: {
      onSuccess: async () => {
        await refetchAssets();
      },
    },
    client: { client: clientWithFork },
  });

  const { mutateAsync: removeAsset } = useAssetControllerRemoveAsset({
    mutation: {
      onSuccess: async () => {
        await refetchAssets();
      },
    },
    client: { client: clientWithFork },
  });

  const { mutateAsync: replaceAsset } = useAssetControllerReplaceAsset({
    mutation: {
      onSuccess: async () => {
        await refetchAssets();
      },
    },
    client: { client: clientWithFork },
  });

  const allRawFields = rawSections
    ? rawSections.flatMap(s => s.fields as FieldShortcut[])
    : rawFields;
  const {
    fields,
    config: assetFieldsConfig,
    isLoading: isProductConfigLoading,
  } = useResolvedEntityFields({
    entityType: 'asset',
    productId: policy.productId,
    fields: allRawFields,
  });
  const resolvedSections = rawSections?.map((s, si) => ({
    title:
      t(`${policy.productId}.assetFunnel.${path}.sections.${si}.title`, { defaultValue: '' }) ||
      undefined,
    fields: resolveFieldShortcuts(s.fields as FieldShortcut[], assetFieldsConfig),
  }));
  const arrayResolver = getArrayFieldsResolver(fields, 'assets', minAssets);

  const form = useForm<{ assets: Asset[] }>({
    mode: 'all',
    values: {
      assets:
        existingAssets?.map(asset =>
          Object.assign(buildFormValuesFromFields(fields, asset) as Asset, { id: asset.id }),
        ) ?? [],
    },
    resolver: arrayResolver,
  });

  const { isDirty } = useFormState({ control: form.control });
  // biome-ignore lint/correctness/useExhaustiveDependencies: stepperRef.current avoids stale closure without creating a dep cycle
  useEffect(() => {
    if (isDirty) stepperRef.current?.invalidateFutureSteps?.();
  }, [isDirty]);

  function scopeFieldsForAsset(assetFields: Field[], index: number): Field[] {
    const scopeConditions = (conditions: Field['showWhen']) =>
      conditions?.map(condition =>
        condition.fieldName
          ? { ...condition, fieldName: `assets.${index}.${condition.fieldName}` }
          : condition,
      ) ?? undefined;
    return assetFields.map(field => ({
      ...field,
      name: `assets.${index}.${field.name}`,
      showWhen: scopeConditions(field.showWhen),
      warningWhen: scopeConditions(field.warningWhen),
      blockingWhen: scopeConditions(field.blockingWhen),
      watchedFieldNames: getWatchedFieldNames(field, index),
    }));
  }

  function getAssetFields(index: number): Field[] {
    return scopeFieldsForAsset(fields, index);
  }

  const {
    fields: formFields,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: 'assets',
    keyName: 'fieldId',
    rules: {
      minLength: minAssets,
      maxLength: maxAssets,
    },
  });

  useEffect(() => {
    if (isAssetsFetched && existingAssets && existingAssets.length === 0) {
      const assets: Partial<Asset>[] = [];
      for (let i = 0; i < minAssets; i++) {
        assets.push(buildFormValuesFromFields(fields, {}));
      }
      form.reset({
        assets,
      });
    }
  }, [existingAssets, form, isAssetsFetched]);

  const handleAddAsset = () => {
    if (formFields.length < maxAssets) {
      append(buildFormValuesFromFields(fields, {}));
      return;
    }
    throw new Error('Max assets reached');
  };

  async function handleRemoveAsset(index: number) {
    if (formFields.length > minAssets) {
      const assetData = form.getValues(`assets.${index}`);

      if (assetData.id) {
        setAssetsToDelete([...assetsToDelete, assetData.id]);
      }

      remove(index);
      return;
    }
    throw new Error('Min assets reached');
  }

  const normalizeRegNumber = (rn: unknown) =>
    typeof rn === 'string' ? rn.replace(/[^A-Za-z0-9]/g, '').toUpperCase() : undefined;

  async function handleNext() {
    const formData = form.getValues();
    const { dirtyFields } = form.formState;

    let mutationBranchId: string | undefined = branchId;

    for (const [index, formAssetData] of formData.assets.entries()) {
      const visibleFields = fields.filter(field => isFieldVisible(field, formAssetData));
      const assetDirtyFields = dirtyFields.assets?.[index] ?? {};

      const payload = buildPayloadFromFields(visibleFields, formAssetData, assetDirtyFields);
      const creationPayload = buildPayloadFromFields(visibleFields, formAssetData);

      const existingAsset = existingAssets?.find(asset => asset.id === formAssetData.id);
      const hasUpdatedRegistrationNumber =
        existingAsset &&
        formAssetData.registrationNumber !== undefined &&
        normalizeRegNumber(existingAsset.customFields.registrationNumber) !==
          normalizeRegNumber(formAssetData.registrationNumber);

      if (
        workingBranch?.branchType === 'MTA' &&
        hasUpdatedRegistrationNumber &&
        !existingAsset.createdOnBranch
      ) {
        const response = await replaceAsset({
          originAssetId: existingAsset.id,
          params: { branchId: mutationBranchId },
          data: {
            policyId,
            name: creationPayload.registrationNumber as string | undefined,
            ...creationPayload,
          },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      } else if (
        workingBranch?.branchType === 'MTA' &&
        hasUpdatedRegistrationNumber &&
        existingAsset.createdOnBranch
      ) {
        const response = await updateAsset({
          assetId: existingAsset.id,
          params: { branchId: mutationBranchId },
          data: {
            name: creationPayload.registrationNumber as string | undefined,
            ...creationPayload,
          },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      } else if (formAssetData.id) {
        if (isEmpty(payload)) {
          continue;
        }

        const response = await updateAsset({
          assetId: formAssetData.id,
          params: { branchId: mutationBranchId },
          data: {
            name: payload.registrationNumber as string | undefined,
            ...payload,
          },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      } else {
        const response = await createAsset({
          params: { branchId: mutationBranchId },
          data: {
            policyId,
            name: creationPayload.registrationNumber as string | undefined,
            ...creationPayload,
          },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    for (const assetId of assetsToDelete) {
      const response = await removeAsset({
        assetId,
        params: { branchId: mutationBranchId },
      });

      const newBranchId = getNewBranchIdAfterFork(response);
      if (newBranchId) {
        mutationBranchId = newBranchId;
      }
    }

    setAssetsToDelete([]);

    if (afterSubmit) {
      const result = await afterSubmit({ policyId, branchId: mutationBranchId ?? branchId });
      mutationBranchId = result?.branchId ?? mutationBranchId;
    }

    if (mode === 'stepper' && stepper) {
      stepper.next(mutationBranchId ? { branchId: mutationBranchId } : undefined);
    }
  }

  if (isAssetsLoading || isProductConfigLoading) {
    return (
      <div className="grid gap-4">
        <div className="max-w-2xl mx-auto">
          <Center className="h-32">
            <div className="text-sm text-muted-foreground">{t('assetFunnel.loading')}</div>
          </Center>
        </div>
      </div>
    );
  }

  const navSections = (resolvedSections ?? [])
    .map((section, si) => ({ si, title: section.title }))
    .filter((section): section is { si: number; title: string } => Boolean(section.title));
  const navAssets = formFields.map((_field, index) => ({
    index,
    label: t([`${policy.productId}.assetFunnel.${path}.itemLabel`, 'assetFunnel.itemLabel'], {
      index: index + 1,
    }),
  }));
  const showNav = showScrollNav && mode === 'stepper' && navAssets.length > 0;

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={
          title ??
          t([
            `${policy.productId}.assetFunnel.${path}.stepTitle`,
            ...(resolvedSections ? [`${policy.productId}.assetFunnel.${path}.title`] : []),
            'funnelSteps.asset.title',
          ])
        }
        subtitle={subtitle ?? t('funnelSteps.asset.subtitle', '')}
      />

      <FunnelWithHelper helper={helper}>
        <Form {...form}>
          <form>
            <Group align="stretch" gap={6}>
              {showNav && <AssetFunnelScrollNav assets={navAssets} sections={navSections} />}
              <Stack className="min-w-0 flex-1">
                {formFields.map((field, index) => {
                  const existingAsset = existingAssets?.find(asset => asset.id === field.id);

                  if (resolvedSections) {
                    return (
                      <Stack key={field.fieldId} id={scrollAnchorId(index)}>
                        {resolvedSections.map((section, si) => (
                          <Card key={si} id={scrollAnchorId(index, si)} className="scroll-mt-24">
                            {section.title && (
                              <CardHeader className="pb-4">
                                <Group justify="between" gap={0}>
                                  <CardTitle>{section.title}</CardTitle>
                                  {shouldShowAutoFill() && (
                                    <SectionAutoFillButton
                                      fields={scopeFieldsForAsset(section.fields, index)}
                                    />
                                  )}
                                </Group>
                              </CardHeader>
                            )}
                            <CardContent>
                              <FormBuilderFields
                                fields={scopeFieldsForAsset(section.fields, index)}
                              />
                            </CardContent>
                          </Card>
                        ))}
                        {formFields.length > minAssets && (
                          <Group align="stretch" justify="end">
                            <Button
                              type="button"
                              variant="destructive-ghost"
                              size="sm"
                              onClick={() => handleRemoveAsset(index)}
                            >
                              <Trash2 className="h-4 w-4 mr-1.5" />
                              {t([`${policy.productId}.assetFunnel.remove`, 'assetFunnel.remove'])}
                            </Button>
                          </Group>
                        )}
                      </Stack>
                    );
                  }

                  return (
                    <Card key={field.fieldId} id={scrollAnchorId(index)} className="scroll-mt-24">
                      <CardHeader className="pb-4">
                        <AssetFormTitle
                          assetData={existingAsset}
                          policy={policy}
                          path={path}
                          assetCount={formFields.length}
                          minAssets={minAssets}
                          handleRemoveAsset={() => handleRemoveAsset(index)}
                          description={description}
                        />
                        {shouldShowAutoFill() && (
                          <SectionAutoFillButton fields={getAssetFields(index)} />
                        )}
                      </CardHeader>

                      <CardContent>
                        <FormBuilderFields fields={getAssetFields(index)} />
                      </CardContent>
                    </Card>
                  );
                })}

                {formFields.length < maxAssets && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddAsset}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t([`${policy.productId}.assetFunnel.addAnother`, 'assetFunnel.addAnother'])}
                  </Button>
                )}
              </Stack>
            </Group>
          </form>
        </Form>
      </FunnelWithHelper>
      <FormBuilderFooter
        mode={mode}
        onSubmit={form.handleSubmit(async () => await handleNext())}
        form={form as UseFormReturn<Record<'assets', unknown>>}
        showPreviousButton
      />
    </Stack>
  );
}
